import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import FramedImage from "@/components/site/FramedImage";
import ReportLightbox from "@/components/site/ReportLightbox";
import { track } from "@/lib/analytics";
import { STRIPE_CONSULTING_URL } from "@/lib/links";

const CONSULT_PAGES = [
  {
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_rrhh-platform-1/artifacts/c937a817f23574c5_CONSUL1.webp",
    label: "Portada del informe ejecutivo",
  },
  {
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_rrhh-platform-1/artifacts/5860ae3d8bb2e83a_CONSUL2.webp",
    label: "Resumen ejecutivo global",
  },
  {
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_rrhh-platform-1/artifacts/b6ae9824c3fe88a0_CONSUL3.webp",
    label: "Riesgos y prioridad estratégica",
  },
  {
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_rrhh-platform-1/artifacts/ae51d3819cf658a8_CONSUL4.webp",
    label: "Recomendaciones prioritarias",
  },
  {
    src: "https://customer-assets-wrfwihn1.emergentagent.net/job_rrhh-platform-1/artifacts/5a9bc65fae15bf31_CONSUL5.webp",
    label: "Entregables finales y plan a 30 / 90 / 180 días",
  },
  {
    src: "/consultoria-checklist-30d.jpg",
    label: "Checklist de implementación · Horizonte 30 días",
  },
  {
    src: "/consultoria-checklist-90d.jpg",
    label: "Checklist de implementación · Horizonte 90 días",
  },
  {
    src: "/consultoria-oferta-empleo.jpg",
    label: "Oferta de empleo · Product Designer",
  },
  {
    src: "/consultoria-guia-entrevista.jpg",
    label: "Guía de entrevista por competencias",
  },
  {
    src: "/consultoria-plan-onboarding.jpg",
    label: "Plan de onboarding",
  },
  {
    src: "/consultoria-contraste-percepciones.jpg",
    label: "Contraste de percepciones CEO vs equipo",
  },
];

const DELIVERABLES = [
  "El diagnóstico rápido incluido sin coste adicional",
  "Cuestionario adicional de 35 preguntas específicas de tu empresa (37 si tu equipo es internacional)",
  "Análisis profundo de las 6 áreas de gestión del talento, que incorpora particularidades de tu sector cuando aplica",
  "Informe profesional personalizado en 24 horas, con base en marcos académicos de gestión del talento y psicología organizacional, y en el marco legal de tu país (España, México, Colombia, Chile, Argentina y Perú)",
  "El informe es tuyo: edítalo y adáptalo antes de compartirlo con tu equipo o con un inversor",
  "Contraste de percepciones: puedes invitar a tu equipo a dar su propia perspectiva de forma anónima, y la plataforma te muestra en qué coincide con tu diagnóstico y en qué te puede estar sorprendiendo",
  "Plan de acción priorizado a 30, 90 y 180 días",
  "Checklist interactiva web editable con seguimiento de progreso",
  "7 herramientas inteligentes que ejecutan nuestra metodología con los datos de tu empresa",
];

const AI_TOOLS = [
  "Generador de ofertas",
  "Asistente de entrevistas",
  "Generador de onboarding",
  "Asistente de evaluación",
  "Asistente de feedback",
  "Generador de planes de desarrollo",
];

const FOR_YOU = [
  "Eres founder de una startup de entre 5 y 100 personas —con especial foco entre 10 y 50— y aún no hay departamento de RRHH.",
  "Ya sabes que algo no funciona pero no tienes tiempo (ni ganas) de convertirte en experto en talento.",
  "Buscas entregables aplicables desde el día uno, no un PDF genérico de 80 páginas.",
  "Quieres estructurar cómo se contrata, se desarrolla y se retiene el talento antes de que sea urgente.",
];

export default function Consulting() {
  const [page, setPage] = useState(null);

  const openGallery = () => {
    track("consulting_report_gallery_open");
    setPage(0);
  };
  return (
    <section
      id="consultoria"
      data-testid="consulting-section"
      className="relative bg-[#06231D] text-[#F7F9F8] py-28 lg:py-40 overflow-hidden grain"
    >
      <div aria-hidden className="absolute bottom-0 right-0 w-[560px] h-[560px] rounded-full blur-[140px] opacity-40" style={{ background: "radial-gradient(circle,#C9A84C 0%, transparent 70%)" }} />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 lg:items-start mb-16 lg:mb-24">
          <Reveal className="lg:col-span-4 lg:self-start">
            <div className="flex items-center gap-4">
              <span className="font-serif-italic text-[#C9A84C] text-[9rem] lg:text-[12rem] leading-none -mt-5 lg:-mt-8 block">05</span>
              <span className="inline-block text-xs lg:text-sm uppercase tracking-[0.18em] font-medium text-[#06231D] bg-[#C9A84C] px-3 py-2 rounded-md">La consultoría</span>
            </div>
          </Reveal>
          <div className="lg:col-span-8 space-y-6">
            <Reveal>
              <div className="text-[#C9A84C] text-[13px] uppercase tracking-[0.22em]">
                Esto no es todo...
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] max-w-[22ch]">
                El diagnóstico te dice <span className="font-serif-italic font-normal text-[#2EC48A]">qué</span>.
                <br />
                <br />
                La consultoría te dice <span className="font-serif-italic font-normal text-[#C9A84C]">cómo</span>.
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="text-white/70 text-lg leading-relaxed max-w-2xl font-light text-justify hyphens-auto">
                Saber dónde están los problemas es el primer paso. El segundo es resolverlos con metodología, entregables concretos y un plan a medida de tu empresa.
              </p>
            </Reveal>
          </div>
        </div>

        <FramedImage
          src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1800&q=80"
          alt="Documentos, informes y entregables aplicables sobre un escritorio."
          variant="wide"
          tint="dark"
          duotone
          fullBleed
          className="mb-16 lg:mb-24"
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: for you if... */}
          <div className="lg:col-span-5 space-y-10">
            <Reveal>
              <div className="border border-[#C9A84C]/30 p-8 lg:p-10 bg-[#C9A84C]/[0.03] rounded-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <Sparkles size={16} strokeWidth={1.5} className="text-[#C9A84C]" />
                  <span className="text-[11px] uppercase tracking-[0.22em] text-[#C9A84C]">
                    Este servicio es para ti si...
                  </span>
                </div>
                <ul className="space-y-5">
                  {FOR_YOU.map((item, i) => (
                    <motion.li
                      key={item}
                      initial={{ opacity: 0, x: -8 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: i * 0.06 }}
                      className="flex items-start gap-4 text-white/85 text-[15px] leading-relaxed font-light"
                    >
                      <span className="mt-2 w-2 h-2 rounded-full bg-[#C9A84C] flex-shrink-0" />
                      {item}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="pt-2">
                <div className="text-[11px] uppercase tracking-[0.24em] text-white/50 mb-3">Un solo pago</div>
                <div className="font-display text-2xl lg:text-3xl text-white leading-tight max-w-[26ch]">
                  Un pago y ya no te preocupas de nada más.
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right: deliverables */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="text-[11px] uppercase tracking-[0.24em] text-white/50 mb-8">
                Consultoría de gestión de talento para startups
              </div>
              <p className="text-white/70 text-base lg:text-lg leading-relaxed font-light max-w-2xl mb-12 text-justify hyphens-auto">
                Para empresas que ya saben dónde están sus puntos críticos y quieren resolverlos con un análisis profesional en profundidad, entregables aplicables desde el día uno y recomendaciones concretas a la realidad de tu compañía.
              </p>
            </Reveal>

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0 border-t border-white/10">
              {DELIVERABLES.map((item, i) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  className="group flex items-start gap-4 py-5 border-b border-white/10 transition-[transform,padding] duration-300 ease-out hover:translate-x-1.5 hover:pl-2"
                  data-testid={`deliverable-${i}`}
                >
                  <span className="mt-1 w-6 h-6 rounded-full border border-[#2EC48A]/40 grid place-items-center flex-shrink-0 group-hover:bg-[#2EC48A] group-hover:border-[#2EC48A] transition-colors">
                    <Check size={12} strokeWidth={2} className="text-[#2EC48A] group-hover:text-[#06231D] transition-colors" />
                  </span>
                  <span className="text-white/85 text-[15px] font-light leading-relaxed">{item}</span>
                </motion.li>
              ))}
            </ul>

            {/* Ampliación de las 6 áreas (sección 2) + nota de anonimato del contraste */}
            <Reveal delay={0.05}>
              <p className="mt-8 text-white/60 text-sm lg:text-[15px] leading-relaxed font-light max-w-2xl text-justify hyphens-auto" data-testid="consulting-areas-ampliadas">
                El análisis de las 6 áreas ahora llega más lejos: recompensas y salario emocional,
                no solo el salario en sí; cómo se gestionan las salidas del equipo y qué se aprende
                de ellas; si las decisiones del día a día dependen demasiado de ti o ya están bien
                delegadas; carga de trabajo, desconexión y riesgos psicosociales; y, si tu equipo es
                internacional, cómo se gestiona la comunicación entre culturas.
              </p>
              <p className="mt-4 text-white/40 text-xs leading-relaxed font-light max-w-2xl" data-testid="consulting-contraste-nota">
                El contraste de percepciones es opcional y se activa a partir de un tamaño mínimo de
                equipo, para garantizar que las respuestas son realmente anónimas.
              </p>
            </Reveal>

            {/* AI tools chips */}
            <Reveal delay={0.1}>
              <div className="mt-10">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#C9A84C] mb-4 flex items-center gap-3">
                  <span className="w-6 h-px bg-[#C9A84C]" />
                  Herramientas inteligentes incluidas
                </div>
                <ul className="flex flex-wrap gap-2">
                  {AI_TOOLS.map((t) => (
                    <li
                      key={t}
                      className="text-[11px] uppercase tracking-[0.14em] text-white/75 border border-white/15 rounded-full px-3 py-1.5 hover:border-[#C9A84C] hover:text-white transition-colors"
                    >
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Premium buy-button CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-20 lg:mt-28"
          data-testid="consulting-cta-block"
        >
          {/* Ambient glow */}
          <div
            aria-hidden
            className="absolute -inset-6 rounded-[28px] blur-2xl opacity-30 brand-gradient pointer-events-none"
          />

          <div className="relative rounded-2xl overflow-hidden border border-[#C9A84C]/50 bg-[linear-gradient(160deg,rgba(201,168,76,0.12),rgba(6,35,29,0.55)_50%,rgba(46,196,138,0.08))] shadow-[0_60px_140px_-40px_rgba(201,168,76,0.35),0_30px_60px_-30px_rgba(6,35,29,0.9)] p-8 md:p-12 lg:p-16">
            {/* Corner gold rules */}
            <div className="absolute top-0 left-0 h-px w-32 brand-gradient" />
            <div className="absolute bottom-0 right-0 h-px w-32 brand-gradient" />
            <div
              aria-hidden
              className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full blur-[110px] opacity-40 brand-gradient pointer-events-none"
            />
            <span className="absolute top-4 left-4 w-4 h-px bg-[#C9A84C]" />
            <span className="absolute top-4 left-4 w-px h-4 bg-[#C9A84C]" />
            <span className="absolute top-4 right-4 w-4 h-px bg-[#C9A84C]" />
            <span className="absolute top-4 right-4 w-px h-4 bg-[#C9A84C]" />
            <span className="absolute bottom-4 left-4 w-4 h-px bg-[#C9A84C]" />
            <span className="absolute bottom-4 left-4 w-px h-4 bg-[#C9A84C]" />
            <span className="absolute bottom-4 right-4 w-4 h-px bg-[#C9A84C]" />
            <span className="absolute bottom-4 right-4 w-px h-4 bg-[#C9A84C]" />

            <div className="relative flex flex-col items-center text-center">
              <div className="flex flex-col items-center">
                <div className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.24em] text-[#C9A84C] mb-6">
                  <span className="w-6 h-px bg-[#C9A84C]" />
                  Consultoría premium · Plazas limitadas
                </div>
                <h3 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.02] text-white max-w-[16ch]">
                  Pon <span className="font-serif-italic font-normal text-[#2EC48A]">orden</span> a tu equipo.
                </h3>
                <p className="mt-6 text-white/70 text-base lg:text-lg leading-relaxed font-light max-w-xl text-justify hyphens-auto">
                  Un pago único de <span className="text-white font-medium">€397 · $460 USD</span>. Informe personalizado listo en 24 horas: la velocidad la pone la IA, el rigor la metodología.
                </p>
                <p className="mt-4 text-white/70 text-base lg:text-lg leading-relaxed font-light max-w-xl text-justify hyphens-auto">
                  Cero reuniones innecesarias, cero burocracia — solo entregables aplicables desde el día uno.
                </p>

                <ul className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3 text-[12px] uppercase tracking-[0.18em] text-white/60">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2EC48A]" />
                    Pago único
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2EC48A]" />
                    Entrega en 24h
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2EC48A]" />
                    100% asíncrono
                  </li>
                </ul>
              </div>

              <div className="mt-10 lg:mt-12 w-full flex flex-col items-center gap-8">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={openGallery}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openGallery();
                    }
                  }}
                  data-testid="consulting-gallery-open"
                  aria-label="Ver un ejemplo de informe ejecutivo real"
                  className="cursor-pointer group w-full flex flex-col items-center"
                >
                  <FramedImage
                    src="https://customer-assets-wrfwihn1.emergentagent.net/job_rrhh-platform-1/artifacts/1810f7690f3651ff_Copy%20of%20Untitled%20Design.webp"
                    alt="Informe ejecutivo de la consultoría premium — portada, checklist de implementación y contraste de percepciones."
                    variant="square"
                    tint="dark"
                    bare
                    shadow
                    className="w-full max-w-[560px] transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                  <p className="mt-5 text-center text-[11px] uppercase tracking-[0.22em] text-[#C9A84C]/80 group-hover:text-[#C9A84C] transition-colors">
                    Haz clic para ver un informe real por dentro
                  </p>
                </div>
                <a
                  href={STRIPE_CONSULTING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="consulting-buy-button"
                  onClick={() => track("cta_click", { location: "consulting_card", product: "consulting" })}
                  className="group inline-flex items-center gap-3 px-10 h-16 rounded-full bg-[#C9A84C] text-[#06231D] text-base font-medium tracking-wide whitespace-nowrap hover:bg-[#D9BC6A] transition-colors cta-lift"
                >
                  Comprar consultoría
                  <span className="w-9 h-9 rounded-full bg-[#06231D]/10 grid place-items-center group-hover:bg-[#06231D]/20 transition-colors">
                    →
                  </span>
                </a>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/40">
                  Pago seguro procesado por Stripe
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {page !== null && (
          <ReportLightbox
            pages={CONSULT_PAGES}
            kicker="Ejemplo de informe real · Consultoría premium"
            testIdPrefix="consulting"
            index={page}
            onClose={() => setPage(null)}
            onPrev={() => setPage((p) => (p - 1 + CONSULT_PAGES.length) % CONSULT_PAGES.length)}
            onNext={() => setPage((p) => (p + 1) % CONSULT_PAGES.length)}
            onSelect={(i) => setPage(i)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
